import { HeaderImage, MediaDisplay } from "../../../../resources/modelTypes";
import couplePhotoDefault from "../../../images/website/photo-couple.png";
import singlePhotoDefault from "../../../images/website/photo-single.png";
import { FullBleedMedia } from "./FullBleedMedia";
import { LandscapeMedia } from "./LandscapeMedia";
import { MarqueeMedia } from "./MarqueeMedia";
import { OverlappingMedia } from "./OverlappingMedia";
import { SideBySideMedia } from "./SideBySideMedia";
import { SlideshowMedia } from "./SlideshowMedia";
import { SquareMedia } from "./SquareMedia";
import {
  getHeaderImageAtPosition,
  getHeaderMediaFrames,
  getPrimaryHeaderImageUrl,
  getPrimaryHeaderSlotPosition,
  resolveHeaderImageUrl,
} from "./getPrimaryHeaderImageUrl";
import React, { memo } from "react";

export type HeaderMediaProps = {
  alt: string;
  /** Legacy site.assets theme image, used when headerImages is empty. */
  fallbackPhotoURL?: string | null;
  /** Legacy site.assets bride image for two-slot layouts when headerImages is empty. */
  fallbackFirstPhotoURL?: string | null;
  /** Legacy site.assets groom image for two-slot layouts when headerImages is empty. */
  fallbackSecondPhotoURL?: string | null;
  headerImages: HeaderImage[];
  mediaDisplay: MediaDisplay;
};

/**
 * Renders the modular header media block for the selected mediaDisplay.
 * All MediaDisplay treatments are implemented; None renders nothing.
 */
const HeaderMedia = ({
  alt,
  fallbackPhotoURL,
  fallbackFirstPhotoURL,
  fallbackSecondPhotoURL,
  headerImages,
  mediaDisplay,
}: HeaderMediaProps) => {
  if (mediaDisplay === MediaDisplay.None) {
    return null;
  }

  if (
    mediaDisplay === MediaDisplay.Square ||
    mediaDisplay === MediaDisplay.Overlapping ||
    mediaDisplay === MediaDisplay.SideBySide
  ) {
    const firstHeaderImage = getHeaderImageAtPosition(headerImages, 0);
    const secondHeaderImage = getHeaderImageAtPosition(headerImages, 1);
    const firstPhotoURL =
      (firstHeaderImage && resolveHeaderImageUrl(firstHeaderImage)) ||
      fallbackFirstPhotoURL ||
      singlePhotoDefault;
    const secondPhotoURL =
      (secondHeaderImage && resolveHeaderImageUrl(secondHeaderImage)) ||
      fallbackSecondPhotoURL ||
      singlePhotoDefault;

    if (mediaDisplay === MediaDisplay.Overlapping) {
      return (
        <OverlappingMedia
          alt={alt}
          backPhotoURL={firstPhotoURL}
          frontPhotoURL={secondPhotoURL}
        />
      );
    }

    if (mediaDisplay === MediaDisplay.SideBySide) {
      return (
        <SideBySideMedia
          alt={alt}
          leftPhotoURL={firstPhotoURL}
          rightPhotoURL={secondPhotoURL}
        />
      );
    }

    return (
      <SquareMedia
        alt={alt}
        leftPhotoURL={firstPhotoURL}
        rightPhotoURL={secondPhotoURL}
      />
    );
  }

  if (
    mediaDisplay === MediaDisplay.Marquee ||
    mediaDisplay === MediaDisplay.Slideshow
  ) {
    const frames = getHeaderMediaFrames(headerImages);
    const resolvedFrames =
      frames.length > 0
        ? frames
        : [{ position: 0, url: fallbackPhotoURL || couplePhotoDefault }];

    if (mediaDisplay === MediaDisplay.Marquee) {
      return <MarqueeMedia alt={alt} frames={resolvedFrames} />;
    }

    return <SlideshowMedia alt={alt} frames={resolvedFrames} />;
  }

  const primaryHeaderSlot = getPrimaryHeaderSlotPosition(headerImages);
  const photoURL =
    getPrimaryHeaderImageUrl(headerImages) ||
    fallbackPhotoURL ||
    couplePhotoDefault;

  switch (mediaDisplay) {
    case MediaDisplay.FullBleed:
      return (
        <FullBleedMedia
          alt={alt}
          headerSlotPosition={primaryHeaderSlot}
          photoURL={photoURL}
        />
      );
    case MediaDisplay.Landscape:
    default:
      return (
        <LandscapeMedia
          alt={alt}
          headerSlotPosition={primaryHeaderSlot}
          photoURL={photoURL}
        />
      );
  }
};

const MemoizedHeaderMedia = memo(HeaderMedia);

export { MemoizedHeaderMedia as HeaderMedia };
